import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Globe, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  nombre: "",
  empresa: "",
  correo: "",
  telefono: "",
  mensaje: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.correo || !form.telefono || !form.mensaje) {
      // defer to avoid DOM reconciliation conflicts with Sonner
      setTimeout(() => {
        toast.error("Por favor completa los campos requeridos.");
      }, 0);
      return;
    }
    if (!acceptedPrivacy) {
      setTimeout(() => {
        toast.error("Debes aceptar el Aviso de Privacidad para continuar.");
      }, 0);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setForm(initialState);
      setAcceptedPrivacy(false);
      setLoading(false);
      setTimeout(() => {
        toast.success("Mensaje enviado. Te contactaremos pronto.");
      }, 0);
    } catch (err) {
      setLoading(false);
      const detail =
        err?.response?.data?.detail ||
        "No pudimos enviar tu mensaje. Intenta nuevamente.";
      setTimeout(() => {
        toast.error(typeof detail === "string" ? detail : "Error al enviar");
      }, 0);
    }
  };

  const infoItems = [
    {
      icon: Phone,
      label: "Teléfono",
      value: "442 431 9100",
      sub: "Lun–Vie · 8:00–18:00",
    },
    {
      icon: Mail,
      label: "Correo",
      value: "contacto@guardaflex.com.mx",
      sub: "Respuesta en menos de 24h",
    },
    {
      icon: MapPin,
      label: "Ubicación",
      value: "Querétaro, QRO., México",
      sub: "Servicio 24/7 para urgencias",
    },
    {
      icon: Globe,
      label: "Cobertura",
      value: "Querétaro local",
      sub: "Parques industriales y capital",
    },
  ];

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="gf-section bg-[#F8FAFC] relative"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left info */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-px bg-[#00B4D8]" />
              <span className="gf-overline">Contáctanos</span>
            </div>
            <h2 className="font-display font-black text-[#0A1B35] text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-6">
              Cuéntanos qué necesitas
              <span className="text-[#00B4D8]"> fabricar o reparar.</span>
            </h2>
            <p className="text-slate-600 text-base leading-relaxed mb-10">
              Comparte los detalles de tu centro de maquinado y el tipo de
              protección que requieres. Un asesor técnico te responderá con
              una cotización a la medida.
            </p>

            <div className="grid sm:grid-cols-2 gap-5">
              {infoItems.map((it) => {
                const Icon = it.icon;
                return (
                  <div
                    key={it.label}
                    data-testid={`contact-info-${it.label.toLowerCase()}`}
                    className="bg-white border border-slate-200 p-5 hover:border-[#00B4D8] transition-colors"
                    style={{ borderRadius: "2px" }}
                  >
                    <div className="w-10 h-10 bg-[#0A1B35] flex items-center justify-center mb-3">
                      <Icon size={16} className="text-[#00B4D8]" />
                    </div>
                    <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
                      {it.label}
                    </div>
                    <div className="font-semibold text-[#0A1B35] text-sm mb-1">
                      {it.value}
                    </div>
                    <div className="text-xs text-slate-500">{it.sub}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              noValidate
              className="bg-white border border-slate-200 p-8 lg:p-10 relative"
              style={{ borderRadius: "2px" }}
            >
              <div className="absolute -top-px left-8 right-8 h-px bg-[#00B4D8]" />

              <div className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#00B4D8] mb-2">
                Formulario · GF-001
              </div>
              <h3 className="font-display font-black text-2xl text-[#0A1B35] mb-8 tracking-tight">
                Solicita tu cotización
              </h3>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="gf-label" htmlFor="nombre">
                    Nombre <span className="text-[#00B4D8]">*</span>
                  </label>
                  <input
                    id="nombre"
                    name="nombre"
                    value={form.nombre}
                    onChange={onChange}
                    data-testid="input-nombre"
                    placeholder="Tu nombre completo"
                    className="gf-input"
                    required
                  />
                </div>
                <div>
                  <label className="gf-label" htmlFor="empresa">
                    Empresa
                  </label>
                  <input
                    id="empresa"
                    name="empresa"
                    value={form.empresa}
                    onChange={onChange}
                    data-testid="input-empresa"
                    placeholder="Razón social"
                    className="gf-input"
                  />
                </div>
                <div>
                  <label className="gf-label" htmlFor="correo">
                    Correo <span className="text-[#00B4D8]">*</span>
                  </label>
                  <input
                    id="correo"
                    name="correo"
                    type="email"
                    value={form.correo}
                    onChange={onChange}
                    data-testid="input-correo"
                    placeholder="tu@empresa.com"
                    className="gf-input"
                    required
                  />
                </div>
                <div>
                  <label className="gf-label" htmlFor="telefono">
                    Teléfono <span className="text-[#00B4D8]">*</span>
                  </label>
                  <input
                    id="telefono"
                    name="telefono"
                    value={form.telefono}
                    onChange={onChange}
                    data-testid="input-telefono"
                    placeholder="+52 ..."
                    className="gf-input"
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="gf-label" htmlFor="mensaje">
                    Describe tu necesidad{" "}
                    <span className="text-[#00B4D8]">*</span>
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    value={form.mensaje}
                    onChange={onChange}
                    data-testid="input-mensaje"
                    placeholder="Modelo de CNC, tipo de protección requerida (guardas, fuelles, limpiadores), medidas si las conoces..."
                    rows={5}
                    className="gf-input resize-none"
                    required
                  />
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <label
                  htmlFor="accept-privacy"
                  className="flex items-start gap-3 cursor-pointer group"
                  data-testid="privacy-consent-wrapper"
                >
                  <input
                    id="accept-privacy"
                    type="checkbox"
                    checked={acceptedPrivacy}
                    onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                    data-testid="privacy-consent-checkbox"
                    className="mt-1 h-4 w-4 shrink-0 accent-[#00B4D8] cursor-pointer"
                    aria-required="true"
                  />
                  <span className="text-xs text-slate-600 leading-relaxed group-hover:text-slate-800 transition-colors">
                    Acepto el{" "}
                    <Link
                      to="/aviso-de-privacidad"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="privacy-consent-link"
                      className="text-[#00B4D8] font-semibold hover:underline"
                    >
                      Aviso de Privacidad
                    </Link>{" "}
                    y autorizo el tratamiento de mis datos personales para
                    responder mi solicitud de cotización o atención técnica.{" "}
                    <span className="text-[#00B4D8]">*</span>
                  </span>
                </label>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-slate-500">
                  Al enviar aceptas ser contactado por nuestro equipo
                  técnico.
                </p>
                <button
                  type="submit"
                  disabled={loading || !acceptedPrivacy}
                  data-testid="contact-submit-button"
                  className="gf-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="inline-flex items-center gap-2">
                    {loading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                    <span>{loading ? "Enviando" : "Enviar Solicitud"}</span>
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
